import type { Square, Color } from '@ccc/shared-types';

export const FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
export type File = (typeof FILES)[number];
export const RANKS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type Rank = (typeof RANKS)[number];

export function sq(file: File, rank: Rank): Square {
  return `${file}${rank}` as Square;
}

export function fileOf(square: Square): File {
  return square[0] as File;
}

export function rankOf(square: Square): Rank {
  return Number(square[1]) as Rank;
}

export function fileIndex(file: File): number {
  return FILES.indexOf(file);
}

export function indexFile(idx: number): File | null {
  if (idx < 0 || idx > 7) return null;
  return FILES[idx]!;
}

export function isOnBoard(file: number, rank: number): boolean {
  return file >= 0 && file <= 7 && rank >= 1 && rank <= 8;
}

export function offsetSquare(square: Square, df: number, dr: number): Square | null {
  const f = fileIndex(fileOf(square)) + df;
  const r = rankOf(square) + dr;
  if (!isOnBoard(f, r)) return null;
  return sq(indexFile(f)!, r as Rank);
}

export function relativeForward(color: Color): number {
  return color === 'white' ? 1 : -1;
}

export function startRankFor(color: Color, kind: 'pawn' | 'back'): Rank {
  if (kind === 'pawn') return color === 'white' ? 2 : 7;
  return color === 'white' ? 1 : 8;
}

export function promotionRankFor(color: Color): Rank {
  return color === 'white' ? 8 : 1;
}

export function inEnemyHalf(square: Square, color: Color): boolean {
  const r = rankOf(square);
  return color === 'white' ? r >= 5 : r <= 4;
}

/** All 64 squares, file-major. */
export function allSquares(): Square[] {
  const out: Square[] = [];
  for (const r of RANKS) for (const f of FILES) out.push(sq(f, r));
  return out;
}

export function squareDistance(a: Square, b: Square): { df: number; dr: number } {
  return {
    df: fileIndex(fileOf(b)) - fileIndex(fileOf(a)),
    dr: rankOf(b) - rankOf(a),
  };
}
